import { User } from "@common/types/user";
import { WORKSPACE_TASK_LIST_CAPABILITIES } from "./taskListCapabilities";
import { DEFAULT_TASK_LIST_QUERY } from "./taskListConstants";
import {
    buildFirstVisitLanding,
    MONTH_END_CLOSE_LANDING,
    TasksLanding,
} from "./tasksLanding";
import {
    isMonthEndCloseViewShown,
    MonthEndCloseViewParams,
} from "./views/monthEndCloseView";
import { MONTH_END_CLOSE_VIEW_ID } from "./views/OrganizationTaskViews";

/** The workspace default query with no view selected, as returning users and drill-down links land. */
export const WORKSPACE_NO_LANDING: TasksLanding = {
    landingViewId: undefined,
    defaultQuery: DEFAULT_TASK_LIST_QUERY,
    initialOverlayFilters: undefined,
};

interface ResolveWorkspaceTasksLandingParams extends MonthEndCloseViewParams {
    /** The current user; `undefined` while it loads. */
    user: Pick<User, "id" | "type"> | undefined;
    /** Closes or close stages haven't answered their first fetch; a failed fetch counts as answered, so retries don't hold the landing. */
    isAwaitingFirstFetch: boolean;
    hasCachedState: boolean;
    /** A drill-down link or pending close id the page applies on mount, or a `?taskId=` link a landing view's filters could hide. */
    hasDrillDownLink: boolean;
    /** The drill-down link's `view` param; only Month-end close is a view a link lands on. */
    linkedViewId: string | null | undefined;
}

/**
 * First visits land on Month-end close when the workspace lists it, otherwise
 * on Assigned to me, or on All tasks for support users, who aren't members.
 * Returning users and drill-down links get no view, unless the link names
 * Month-end close and the workspace lists it. `null` while the inputs the
 * answer depends on load.
 */
export function resolveWorkspaceTasksLanding({
    user,
    isAwaitingFirstFetch,
    hasCachedState,
    hasDrillDownLink,
    linkedViewId,
    ...monthEndCloseViewParams
}: ResolveWorkspaceTasksLandingParams): TasksLanding | null {
    if (user === undefined) {
        return null;
    }
    const isLinkedToMonthEndClose = linkedViewId === MONTH_END_CLOSE_VIEW_ID;
    const hasLinkOrCachedState = hasCachedState || hasDrillDownLink;
    if (hasLinkOrCachedState && !isLinkedToMonthEndClose) {
        return WORKSPACE_NO_LANDING;
    }
    if (isAwaitingFirstFetch) {
        return null;
    }
    if (isMonthEndCloseViewShown(monthEndCloseViewParams)) {
        return MONTH_END_CLOSE_LANDING;
    }

    if (hasLinkOrCachedState) {
        return WORKSPACE_NO_LANDING;
    }
    return buildFirstVisitLanding({
        user,
        defaultStatuses: WORKSPACE_TASK_LIST_CAPABILITIES.defaultStatuses,
    });
}
